/**
 * Watched-coverage completion for self-hosted video journey map steps.
 *
 * A video step plays in a native `<video>` element, so — unlike an embedded iframe — its media
 * events give us a real per-learner signal for how much has actually been watched. The player
 * reports that coverage to `POST db/journey-map-item/{id}/video-progress/`, which records it and
 * credits completion once coverage crosses the item's threshold.
 *
 * The completion threshold is enforced server-side (from `provider_config.completion_threshold_pct`),
 * and coverage only ever moves up on the server, so a heartbeat that reports a lower number after a
 * seek-back cannot un-complete a step. This is the same trust level as external-launch tracking:
 * the coverage number is client-reported, but the decision to credit is the server's.
 *
 * As with the launch endpoint, the API resolves the caller's active assignment itself when
 * `assignmentId` is omitted, so this is safe to call from a player that has not loaded the
 * assignment yet.
 */
import { buildApiUrl, getApiHeaders } from '../queriesHelpers';

export type JourneyMapVideoProgressResult = {
  /** The `JourneyMapItemProgress` row as the API serializes it. */
  progress: any;
  /** True once the item is credited (either just now or on a previous call). */
  completed: boolean;
  /** True only on the call that crossed the threshold, so the caller can refresh exactly once. */
  newlyCompleted: boolean;
  /** Monotonic max watched coverage the server now holds, in percent. */
  coveragePct: number;
  /** The threshold the server enforced, in percent. */
  thresholdPct: number;
  /**
   * Last playhead the server now holds, in seconds (from `evidence.video.last_position_sec`), or
   * null when none is recorded yet. This is the value to resume from on the next open.
   */
  lastPositionSec: number | null;
};

export type RecordVideoProgressInput = {
  coveragePct: number;
  positionSec?: number | null;
  durationSec?: number | null;
  assignmentId?: number | string | null;
};

export async function recordJourneyMapVideoProgress(
  journeyMapItemId: number | string,
  input: RecordVideoProgressInput,
): Promise<JourneyMapVideoProgressResult> {
  const endpoint = `db/journey-map-item/${journeyMapItemId}/video-progress/`;

  const body: Record<string, any> = {
    coverage_pct: input.coveragePct,
  };

  if (input.positionSec != null) body.position_sec = input.positionSec;
  if (input.durationSec != null) body.duration_sec = input.durationSec;
  if (input.assignmentId != null && input.assignmentId !== '') {
    body.assignment = input.assignmentId;
  }

  const response = await fetch(buildApiUrl(endpoint, {}, true), {
    method: 'POST',
    headers: getApiHeaders(null, 'POST', endpoint),
    credentials: 'include',
    body: JSON.stringify(body),
  });

  const text = await response.text();

  let parsed: any = null;

  try {
    parsed = text ? JSON.parse(text) : null;
  } catch {
    parsed = text;
  }

  if (!response.ok) {
    const error: any = new Error(
      String(
        parsed?.detail || parsed || `Unable to record video progress (${response.status}).`,
      ),
    );
    error.status = response.status;
    error.payload = parsed;
    throw error;
  }

  const video = parsed?.video || {};
  // The last playhead is not in the `video` convenience block; it rides the serialized progress
  // row under `evidence.video.last_position_sec` (present on both the POST response and the
  // journey-map-item-progress list the page fetches on open).
  const rawPosition = Number(parsed?.evidence?.video?.last_position_sec);

  return {
    progress: parsed || null,
    completed: video.completed === true,
    newlyCompleted: video.newly_completed === true,
    coveragePct: Number(video.coverage_pct ?? 0),
    thresholdPct: Number(video.threshold_pct ?? 0),
    lastPositionSec: Number.isFinite(rawPosition) ? rawPosition : null,
  };
}

export const DEFAULT_VIDEO_COMPLETION_THRESHOLD_PCT = 90;

/**
 * Watched-coverage percentage from the set of distinct whole seconds that have played.
 *
 * Coverage is `distinct watched seconds / ceil(duration)`, capped at 100. Counting distinct
 * whole seconds (rather than the furthest playhead position) is what makes the number resistant
 * to seeking: scrubbing backward re-marks seconds already in the set and cannot inflate the
 * count, and skipping forward never marks the skipped seconds. `ceil(duration)` matches the
 * granularity of the marks (`floor(currentTime)`), so a fully watched video reaches 100.
 */
export function computeCoveragePct(
  watchedSeconds: Set<number> | number,
  durationSec: number,
): number {
  if (!Number.isFinite(durationSec) || durationSec <= 0) return 0;
  const watched = typeof watchedSeconds === 'number' ? watchedSeconds : watchedSeconds.size;
  if (watched <= 0) return 0;
  return Math.min(100, (watched / Math.ceil(durationSec)) * 100);
}
